/**
 * VistoeraTASK-00631 / 00630 / 00633 — SaaS Platform Admin · 套餐功能授权矩阵.
 *
 * Reads/writes Phase 1 APIs only:
 *   GET  /platform/billing/plans
 *   GET  /platform/auth-matrix/plans/{plan_key}/matrix
 *   PUT  /platform/auth-matrix/plans/{plan_key}/grants
 *   PATCH /platform/billing/plans/{plan_key} (default flag only)
 *
 * TASK-00633: node-select UX — channels shown only for selectedNodeId;
 * draft stays plan-scoped across node switches (no confirm on node click).
 *
 * Does not touch workspace overrides or switch enforcement mode.
 */
import { Alert, Button, Input, Modal, Space, Switch, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  platformGetAuthPlanMatrix,
  platformListBillingPlans,
  platformPutAuthPlanGrant,
  platformUpdateBillingPlan,
} from '../api'
import type { AuthPlanMatrixResponse, BillingPlan } from '../types'
import { SectionCard, SummaryCard } from './adminShared'
import AuthMatrixTable from './authMatrix/AuthMatrixTable'
import {
  countAuthorized,
  dirtyEntries,
  grantKey,
  isDirty,
  mapsFromPlanRows,
} from './authMatrix/authMatrixDraft'
import { platformColors } from './theme'
import './platform-tables.css'

function formatPrice(plan: BillingPlan): string {
  const cny = plan.prices?.find((p) => p.currency === 'CNY' && p.enabled)
  if (!cny) return '—'
  const yuan = (Number(cny.amount_minor) || 0) / 100
  return `¥${yuan % 1 === 0 ? yuan.toFixed(0) : yuan.toFixed(2)}/月`
}

export default function PlanAuthMatrixPage() {
  const [params, setParams] = useSearchParams()
  const selectedKey = params.get('plan_key')

  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [matrix, setMatrix] = useState<AuthPlanMatrixResponse | null>(null)
  const [baseline, setBaseline] = useState<Record<string, string | null>>({})
  const [draft, setDraft] = useState<Record<string, 'ALLOW' | 'DENY'>>({})
  const [nodeIds, setNodeIds] = useState<string[]>([])
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [loadingPlans, setLoadingPlans] = useState(true)
  const [loadingMatrix, setLoadingMatrix] = useState(false)
  const [saving, setSaving] = useState(false)
  const [defaultSaving, setDefaultSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadSeq = useRef(0)
  const draftRef = useRef(draft)
  const baselineRef = useRef(baseline)
  draftRef.current = draft
  baselineRef.current = baseline

  const dirty = useMemo(() => isDirty(baseline, draft), [baseline, draft])
  const stats = useMemo(
    () => countAuthorized(draft, nodeIds, channelIds),
    [draft, nodeIds, channelIds],
  )

  const selectedPlan = useMemo(
    () => plans.find((p) => p.key === selectedKey) ?? plans[0] ?? null,
    [plans, selectedKey],
  )

  const setSelectedKey = useCallback(
    (key: string) => {
      const next = new URLSearchParams(params)
      next.set('plan_key', key)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const loadPlans = useCallback(() => {
    setLoadingPlans(true)
    platformListBillingPlans()
      .then((r) => {
        const sorted = [...r.data].sort((a, b) => a.sort_order - b.sort_order)
        setPlans(sorted)
        if (!selectedKey && sorted.length > 0) {
          setSelectedKey(sorted[0].key)
        } else if (selectedKey && sorted.length > 0 && !sorted.some((p) => p.key === selectedKey)) {
          setSelectedKey(sorted[0].key)
        }
      })
      .catch((err) => setError((err as Error).message || '加载套餐失败'))
      .finally(() => setLoadingPlans(false))
  }, [selectedKey, setSelectedKey])

  useEffect(() => {
    loadPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const applyMatrix = useCallback(
    (data: AuthPlanMatrixResponse, opts?: { preserveSelection?: boolean }) => {
      setMatrix(data)
      const maps = mapsFromPlanRows(data.rows || [])
      setBaseline(maps.baseline)
      setDraft(maps.draft)
      setNodeIds(maps.nodeIds)
      setChannelIds(maps.channelIds)
      // TASK-00633: first load / plan switch → no default node expansion.
      // After save, keep the same selected node when it still exists.
      setSelectedNodeId((prev) => {
        if (opts?.preserveSelection && prev && maps.nodeIds.includes(prev)) return prev
        return null
      })
    },
    [],
  )

  const loadMatrix = useCallback(
    (planKey: string) => {
      const seq = ++loadSeq.current
      setLoadingMatrix(true)
      setError(null)
      setSelectedNodeId(null)
      platformGetAuthPlanMatrix(planKey)
        .then((r) => {
          if (seq !== loadSeq.current) return
          applyMatrix(r.data)
        })
        .catch((err) => {
          if (seq !== loadSeq.current) return
          setMatrix(null)
          setSelectedNodeId(null)
          setError((err as Error).message || '加载授权矩阵失败')
        })
        .finally(() => {
          if (seq !== loadSeq.current) return
          setLoadingMatrix(false)
        })
    },
    [applyMatrix],
  )

  useEffect(() => {
    if (selectedPlan?.key) loadMatrix(selectedPlan.key)
  }, [selectedPlan?.key, loadMatrix])

  const save = useCallback(async (): Promise<boolean> => {
    if (!selectedPlan) return false
    const changes = dirtyEntries(baselineRef.current, draftRef.current)
    if (changes.length === 0) {
      message.info('没有需要保存的修改')
      return true
    }
    setSaving(true)
    try {
      for (const change of changes) {
        await platformPutAuthPlanGrant(selectedPlan.key, {
          subject_kind: change.kind,
          subject_id: change.subjectId,
          decision: change.decision,
        })
      }
      const verified = await platformGetAuthPlanMatrix(selectedPlan.key)
      applyMatrix(verified.data, { preserveSelection: true })
      message.success(`已保存 ${changes.length} 项套餐授权`)
      return true
    } catch (err) {
      // Keep draft on failure — do not mark as synced.
      message.error(`保存失败：${(err as Error).message}`)
      return false
    } finally {
      setSaving(false)
    }
  }, [applyMatrix, selectedPlan])

  const requestSelectPlan = (key: string) => {
    if (key === selectedPlan?.key) return
    if (!isDirty(baselineRef.current, draftRef.current)) {
      setSelectedKey(key)
      return
    }
    // TASK-00631 §六 — 保存修改 / 放弃修改 / 取消切换
    const modal = Modal.confirm({
      title: '未保存的修改',
      content: '当前套餐授权尚未保存。请选择如何处理后再切换。',
      footer: (
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => {
              modal.destroy()
            }}
          >
            取消切换
          </Button>
          <Button
            onClick={() => {
              modal.destroy()
              setSelectedKey(key)
            }}
          >
            放弃修改
          </Button>
          <Button
            type="primary"
            loading={saving}
            onClick={() => {
              void (async () => {
                const ok = await save()
                if (!ok) return
                modal.destroy()
                setSelectedKey(key)
              })()
            }}
          >
            保存并切换
          </Button>
        </Space>
      ),
    })
  }

  const onNodeToggle = (nodeId: string, allowed: boolean) => {
    setDraft((prev) => ({ ...prev, [grantKey('node', nodeId)]: allowed ? 'ALLOW' : 'DENY' }))
  }

  const onChannelToggle = (channelId: string, allowed: boolean) => {
    setDraft((prev) => ({
      ...prev,
      [grantKey('channel', channelId)]: allowed ? 'ALLOW' : 'DENY',
    }))
  }

  const resetDraft = () => {
    if (!matrix) return
    applyMatrix(matrix)
    message.info('已重置未保存修改')
  }

  const setAsDefault = async (checked: boolean) => {
    if (!selectedPlan || !checked) {
      // Turning off default is not supported as "no default" — direct admin to plan-credits.
      if (!checked) {
        message.info('取消默认套餐请在「套餐与积分」中指定其他套餐为默认')
      }
      return
    }
    if (selectedPlan.is_registration_default) return
    setDefaultSaving(true)
    try {
      const updated = await platformUpdateBillingPlan(selectedPlan.key, {
        is_registration_default: true,
      })
      setPlans((prev) =>
        prev.map((p) =>
          p.key === updated.data.key
            ? updated.data
            : { ...p, is_registration_default: false },
        ),
      )
      message.success('已设为默认套餐')
    } catch (err) {
      message.error(`设置默认套餐失败：${(err as Error).message}`)
    } finally {
      setDefaultSaving(false)
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            计费管理 / 套餐与积分 / 套餐功能授权
          </Typography.Text>
          <Typography.Title level={4} style={{ margin: '4px 0 0' }}>
            套餐功能授权矩阵
          </Typography.Title>
          <Typography.Text type="secondary">
            为不同会员 / 团队套餐设置默认功能节点与通道授权。用户或团队选择套餐后自动继承；本页不修改
            Workspace 人工覆盖，也不切换 enforce。
          </Typography.Text>
        </div>
        <Space wrap>
          <Link to="/platform/plan-credits?tab=plans">套餐与积分</Link>
          <Link to="/platform/plan-credits?tab=plans">
            <Button type="primary">+ 新建套餐</Button>
          </Link>
        </Space>
      </div>

      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} closable onClose={() => setError(null)} />
      )}

      <Space wrap style={{ marginBottom: 12 }}>
        <SummaryCard title="套餐数" value={plans.length} iconBg={platformColors.primarySoft} icon="📦" loading={loadingPlans} />
        <SummaryCard
          title="已授权节点"
          value={selectedPlan ? stats.nodes : '—'}
          iconBg={platformColors.successBg}
          icon="☑"
          loading={loadingMatrix}
        />
        <SummaryCard
          title="已授权通道"
          value={selectedPlan ? stats.channels : '—'}
          iconBg={platformColors.infoBg}
          icon="⇄"
          loading={loadingMatrix}
        />
        <SummaryCard
          title="执行模式"
          value={matrix?.enforcement_mode || '—'}
          hint="Preview 应保持 compat"
          iconBg={platformColors.warningBg}
          icon="⚙"
          loading={loadingMatrix}
        />
      </Space>

      <SectionCard title="选择套餐" allowOverflow>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '4px 0 8px' }}>
          {plans.map((plan) => {
            const selected = plan.key === selectedPlan?.key
            return (
              <button
                key={plan.key}
                type="button"
                onClick={() => requestSelectPlan(plan.key)}
                style={{
                  minWidth: 148,
                  textAlign: 'left',
                  border: selected ? `2px solid ${platformColors.primary}` : `1px solid ${platformColors.border}`,
                  borderRadius: 10,
                  background: selected ? platformColors.primarySoft : platformColors.surface,
                  color: platformColors.primaryText,
                  cursor: 'pointer',
                  padding: '12px 14px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                  <strong style={{ fontSize: 14 }}>{plan.display_name}</strong>
                  {plan.is_registration_default ? <Tag color="green">默认</Tag> : null}
                </div>
                <div style={{ fontSize: 12, color: platformColors.secondaryText }}>{formatPrice(plan)}</div>
                <div style={{ fontSize: 11, color: platformColors.mutedText, marginTop: 4 }}>
                  {plan.enabled ? '启用' : '停用'} · {plan.key}
                </div>
              </button>
            )
          })}
          {!loadingPlans && plans.length === 0 ? (
            <Typography.Text type="secondary">暂无套餐。请先在「套餐与积分」中创建。</Typography.Text>
          ) : null}
        </div>
      </SectionCard>

      {selectedPlan ? (
        <SectionCard
          title={
            <Space>
              <span>{selectedPlan.display_name} 套餐配置</span>
              {selectedPlan.is_registration_default ? <Tag color="green">默认</Tag> : null}
            </Space>
          }
          extra={
            <Space>
              {dirty ? <Tag color="orange">未保存</Tag> : <Tag>已同步</Tag>}
              <Button disabled={!dirty || saving} onClick={resetDraft}>
                重置
              </Button>
              <Button type="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
                保存更改
              </Button>
            </Space>
          }
          allowOverflow
        >
          {selectedPlan.description ? (
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              {selectedPlan.description}
            </Typography.Paragraph>
          ) : null}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignItems: 'center',
              marginBottom: 16,
              padding: '10px 12px',
              border: `1px solid ${platformColors.border}`,
              borderRadius: 8,
              background: platformColors.surface,
            }}
          >
            <div style={{ minWidth: 200, flex: 1 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                套餐名称
              </Typography.Text>
              <Input value={selectedPlan.display_name} disabled readOnly />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                名称与价格请在{' '}
                <Link to={`/platform/plan-credits?tab=plans&plan_key=${encodeURIComponent(selectedPlan.key)}`}>
                  套餐与积分
                </Link>{' '}
                编辑（不在本页另建套餐系统）。
              </Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>设为默认套餐</span>
              <Switch
                checked={Boolean(selectedPlan.is_registration_default)}
                loading={defaultSaving}
                onChange={(v) => void setAsDefault(v)}
              />
            </div>
          </div>

          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            功能授权矩阵
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            点击左侧功能节点后，右侧仅展示该节点已映射的 Provider 通道（provider_option_id）。节点切换保留未保存
            Draft；保存后统一提交。
          </Typography.Text>
          <AuthMatrixTable
            rows={matrix?.rows || []}
            draft={draft}
            selectedNodeId={selectedNodeId}
            onSelectNode={setSelectedNodeId}
            onNodeToggle={onNodeToggle}
            onChannelToggle={onChannelToggle}
            loading={loadingMatrix}
          />
        </SectionCard>
      ) : null}
    </div>
  )
}
